/** A point on the page — absolute coordinates relative to the document body. */
export interface MarkerPoint {
  x: number;
  y: number;
}

/** A viewport size to position a marker against. */
export interface Viewport {
  width: number;
  height: number;
}

/** The marker fields needed to reposition it (mirrors App\Models\Marker). */
interface MarkerData {
  x: number;
  y: number;
  screen: Viewport;
}

/**
 * Decides where to place a feedback marker on the CURRENT page, given marker data
 * captured on a (possibly different-sized) screen. Used when feedback is retrieved
 * from the API to re-position existing markers for the current viewport.
 *
 * Stored x/y are absolute DOCUMENT coordinates (captured as `clientX + scrollX` /
 * `clientY + scrollY`), while `screen` is the VIEWPORT that was visible at the time.
 * Those two spaces behave very differently when the viewport changes:
 *
 * - **Horizontally**, page content reflows and re-centres with the viewport width,
 *   so `x` is scaled by the width ratio to land at the equivalent spot.
 * - **Vertically**, it does not. Content flows from the top of the document, so an
 *   element 2000px down stays 2000px down no matter how tall the window is — a
 *   taller viewport just reveals more of the page. `y` is therefore used AS-IS.
 *
 * If the current viewport width is within **40%–160%** of the capture width, the
 * marker is placed; beyond that the horizontal scaling is too rough to trust and
 * the marker is skipped (`resolve()` returns `null`). Height is deliberately not
 * gated — it no longer affects the result, and gating on it used to hide markers
 * that were positioned perfectly well (e.g. a 1440x653 capture viewed on a
 * 1900x1050 monitor, where only the height had "changed" enough to fail).
 */
export class MarkerPositioner {
  /**
   * Maximum viewport WIDTH difference (as a fraction of the capture width) for which
   * we still place the marker — 0.6 covers the 40%–160% range.
   */
  private static readonly MAX_DIFFERENCE = 0.6;

  /**
   * Resolve a marker's position on the current page.
   *
   * @param rawMarker The marker object from a feedback item (loosely typed).
   * @param viewport  The current viewport size (defaults to the window).
   * @returns Page coordinates to place the marker, or `null` to skip it.
   */
  static resolve(rawMarker: unknown, viewport: Viewport = MarkerPositioner.currentViewport()): MarkerPoint | null {
    const marker = MarkerPositioner.parse(rawMarker);
    if (!marker) {
      return null;
    }
    if (MarkerPositioner.widthDifference(marker.screen, viewport) > MarkerPositioner.MAX_DIFFERENCE) {
      return null;
    }
    return MarkerPositioner.repositioned(marker, viewport);
  }

  /** The current window viewport size. */
  static currentViewport(): Viewport {
    return { width: window.innerWidth, height: window.innerHeight };
  }

  /**
   * How far the current viewport width is from the capture width, as a fraction of
   * the capture width (e.g. 0.25 = 25% narrower or wider).
   */
  private static widthDifference(screen: Viewport, viewport: Viewport): number {
    return Math.abs(viewport.width - screen.width) / screen.width;
  }

  /**
   * Maps captured page coordinates onto the current viewport: `x` scales with the
   * width ratio (content reflows horizontally), `y` is preserved because a
   * document-absolute offset from the top does not change with viewport height.
   */
  private static repositioned(marker: MarkerData, viewport: Viewport): MarkerPoint {
    return {
      x: Math.round(marker.x * (viewport.width / marker.screen.width)),
      y: Math.round(marker.y),
    };
  }

  /** Extracts the fields we need from a loosely-typed marker; null if unusable. */
  private static parse(rawMarker: unknown): MarkerData | null {
    if (!rawMarker || typeof rawMarker !== 'object') {
      return null;
    }
    const record = rawMarker as Record<string, unknown>;
    const x = Number(record['x']);
    const y = Number(record['y']);
    const screen = MarkerPositioner.screenOf(record['screen']);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !screen) {
      return null;
    }
    return { x, y, screen };
  }

  /** Normalises a stored screen (tuple `[w, h]` or `{ width, height }`) to a Viewport. */
  private static screenOf(screen: unknown): Viewport | null {
    if (Array.isArray(screen) && screen.length >= 2) {
      const width = Number(screen[0]);
      const height = Number(screen[1]);
      return width > 0 && height > 0 ? { width, height } : null;
    }
    if (screen && typeof screen === 'object') {
      const record = screen as Record<string, unknown>;
      const width = Number(record['width']);
      const height = Number(record['height']);
      return width > 0 && height > 0 ? { width, height } : null;
    }
    return null;
  }
}
